import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { HRPromoCard } from '../../components/HRPromoBanner';
import {
  ArrowRight, CheckCircle2, Plug, Zap, Clock,
  FileText, Shield, BarChart3, Receipt, Scale,
  Link2, Settings, Headphones, CalendarDays,
  BookOpen, ChevronRight, Layers, MonitorSmartphone
} from 'lucide-react';

import beds24Logo from '../../assets/images/partners/beds24.jpeg';
import siteminderLogo from '../../assets/images/partners/siteminder.jpeg';
import littleHotelierLogo from '../../assets/images/partners/little-hotelier.jpeg';
import guestyLogo from '../../assets/images/partners/guesty.jpeg';

/* ─── scroll-reveal ─── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('revealed'); obs.unobserve(el); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal-el ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

/* ─── Positioning Line ─── */
function PositioningBanner() {
  return (
    <div className="py-5 bg-[#0F172A]">
      <p className="text-center text-white/60 text-sm font-medium tracking-wide">
        <Plug className="inline h-4 w-4 mr-2 text-blue-400 -mt-0.5" />
        StayMii integrates with your PMS. No system change required.
      </p>
    </div>
  );
}

/* ─── Partner logos ─── */
const partners = [
  { name: 'Beds24', logo: beds24Logo },
  { name: 'SiteMinder', logo: siteminderLogo },
  { name: 'Little Hotelier', logo: littleHotelierLogo },
  { name: 'Guesty', logo: guestyLogo },
];

const GetStarted = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="get-started-page">
      <SEO
        title="Get Started - StayMii E-Invoice & Compliance Setup"
        description="Set up StayMii with your existing PMS or let us help you choose. Go live fast with LHDN-compliant e-invoicing. No migration required."
        keywords="hotel e-invoice setup, lhdn compliance onboarding, pms integration malaysia, staymii get started"
        canonicalUrl="/get-started"
      />
      <LandingNavBar />

      {/* ═══════════════════════════════════════════════════════
          SECTION 1 — HERO
      ═══════════════════════════════════════════════════════ */}
      <section className="relative pt-28 pb-20 lg:pt-36 lg:pb-28 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] overflow-hidden" data-testid="get-started-hero">
        {/* decorative blurs */}
        <div className="absolute top-10 left-[10%] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-[15%] w-[400px] h-[400px] bg-indigo-500/8 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-testid="hero-headline">
              Let's Get You Started
            </h1>
          </Reveal>

          <Reveal delay={80}>
            <p className="text-lg sm:text-xl text-white/75 max-w-3xl mx-auto mb-10 leading-relaxed" data-testid="hero-subheadline">
              Whether you already have a system or are starting from scratch, StayMii helps you get set up quickly
              — with seamless integration and compliant e-invoicing from day one.
            </p>
          </Reveal>

          <Reveal delay={140}>
            <div className="flex flex-wrap justify-center gap-5 mb-8">
              {[
                { icon: Plug, text: 'Works with your existing PMS or channel manager' },
                { icon: Settings, text: "No system? We'll help you choose and set up the right one" },
                { icon: Zap, text: 'Go live fast with LHDN-compliant e-invoicing' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5 text-white/70 text-sm">
                  <item.icon className="h-4 w-4 text-blue-400 shrink-0" />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={200}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-semibold text-lg transition-all hover:shadow-xl hover:shadow-blue-600/20"
                data-testid="hero-cta-primary"
              >
                Get Started <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-xl font-semibold text-lg hover:bg-white/15 transition-all"
                data-testid="hero-cta-secondary"
              >
                <Headphones className="h-5 w-5" /> Book a Consultation
              </Link>
            </div>
          </Reveal>

          <Reveal delay={260}>
            <p className="text-white/40 text-sm font-medium" data-testid="hero-trust-line">
              <Plug className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
              StayMii integrates with your PMS. No system change required.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          SECTION 2 — FLEXIBLE SETUP
      ═══════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28 bg-white" data-testid="flexible-setup-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full text-[#2563EB] text-sm font-medium mb-4">
                <Settings className="h-4 w-4" /> Flexible Setup
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4" data-testid="setup-headline">
                Use Your Current System — Or Let Us Help You Choose
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Option A */}
            <Reveal delay={60}>
              <div className="relative bg-white border-2 border-blue-200 rounded-2xl p-8 hover:shadow-xl transition-all h-full" data-testid="setup-option-a">
                <div className="absolute -top-3.5 left-6">
                  <span className="px-4 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-full uppercase tracking-wide">
                    Option A
                  </span>
                </div>
                <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-6 mt-2">
                  <Plug className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Already Have a PMS?</h3>
                <p className="text-slate-600 mb-2 leading-relaxed">
                  StayMii integrates with widely used hospitality systems such as Beds24, Little Hotelier, and Guesty.
                </p>
                <p className="text-slate-500 text-sm mb-5 leading-relaxed">
                  Already using a system? We'll connect StayMii to your existing setup.
                </p>
                <ul className="space-y-3 mb-6">
                  {['No migration required', 'No disruption to operations', 'Go live quickly'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700">
                      <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                      <span className="text-sm font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
                {/* PMS logos */}
                <div className="pt-5 border-t border-slate-100">
                  <p className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold mb-3">Integrates with</p>
                  <div className="flex flex-wrap gap-3 items-center">
                    {partners.map((p) => (
                      <img
                        key={p.name}
                        src={p.logo}
                        alt={p.name}
                        className="h-8 w-auto object-contain rounded"
                        data-testid={`option-a-logo-${p.name.toLowerCase().replace(/\s/g, '-')}`}
                      />
                    ))}
                  </div>
                </div>
                {/* Positioning line */}
                <p className="mt-5 text-xs text-blue-600/70 font-medium">
                  <Plug className="inline h-3 w-3 mr-1 -mt-0.5" />
                  StayMii integrates with your PMS. No system change required.
                </p>
              </div>
            </Reveal>

            {/* Option B */}
            <Reveal delay={120}>
              <div className="relative bg-white border-2 border-slate-200 rounded-2xl p-8 hover:shadow-xl transition-all h-full" data-testid="setup-option-b">
                <div className="absolute -top-3.5 left-6">
                  <span className="px-4 py-1.5 bg-slate-700 text-white text-xs font-bold rounded-full uppercase tracking-wide">
                    Option B
                  </span>
                </div>
                <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mb-6 mt-2">
                  <Headphones className="h-7 w-7 text-slate-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">No PMS Yet?</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  We help you select, set up, and configure the right PMS or channel manager for your property.
                </p>
                <ul className="space-y-3">
                  {['System recommendation', 'Setup & onboarding', 'Property configuration', 'Integration with StayMii'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700">
                      <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                      <span className="text-sm font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Positioning Line ── */}
      <PositioningBanner />

      {/* ═══════════════════════════════════════════════════════
          SECTION 3 — STAYMII CORE (VISUALLY DOMINANT)
      ═══════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-[#0F172A] via-[#162036] to-[#0F172A] relative overflow-hidden" data-testid="core-section">
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-blue-600/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/15 rounded-full text-blue-400 text-sm font-medium mb-4 border border-blue-500/20">
                <Shield className="h-4 w-4" /> Core Product
              </span>
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-5" data-testid="core-headline">
                StayMii Handles Compliance & E-Invoicing
              </h2>
              <p className="text-lg text-white/60 max-w-2xl mx-auto">
                Focus on running your property. StayMii automates the compliance layer so you don't have to.
              </p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Receipt, title: 'E-Invoicing Aligned with LHDN', desc: 'Fully compliant invoices that meet Malaysia\'s MyInvois e-invoicing mandate.' },
              { icon: Zap, title: 'Automated Invoice Generation', desc: 'Invoices auto-generated from bookings. No manual data entry.' },
              { icon: Scale, title: 'Submission to Tax System', desc: 'Direct submission to LHDN with validation, error handling, and audit trail.' },
              { icon: BarChart3, title: 'Financial & Audit Reporting', desc: 'Dashboard with real-time submission status, analytics, and exportable reports.' },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-7 hover:bg-white/10 transition-all h-full group" data-testid={`core-feature-${i}`}>
                  <div className="w-12 h-12 bg-blue-600/20 rounded-xl flex items-center justify-center mb-5 group-hover:bg-blue-600/30 transition-colors">
                    <item.icon className="h-6 w-6 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Trust line */}
          <Reveal delay={300}>
            <p className="text-center text-white/35 text-sm font-medium mt-12">
              <Plug className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
              StayMii integrates with your PMS. No system change required.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          SECTION 4 — STAYMII LITE
      ═══════════════════════════════════════════════════════ */}
      <section className="py-16 lg:py-20 bg-slate-50" data-testid="lite-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="bg-white rounded-2xl border border-slate-200 p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                  <MonitorSmartphone className="h-5 w-5 text-slate-500" />
                </div>
                <span className="px-3 py-1 bg-slate-100 text-slate-500 text-xs font-semibold rounded-full uppercase tracking-wide">
                  Optional Tool
                </span>
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-3" data-testid="lite-headline">
                No PMS? Use StayMii Lite
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed max-w-2xl">
                StayMii Lite is a lightweight tool for operators without an existing system.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  'Manual booking creation',
                  'Calendar updates',
                  'Basic booking records',
                  'Integrated e-invoicing',
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-slate-400 shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-slate-100">
                <Link to="/features#staymii-lite" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 font-medium transition-colors" data-testid="lite-learn-more">
                  Learn more about StayMii Lite <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          SECTION 5 — PARTNER ECOSYSTEM
      ═══════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-24 bg-white" data-testid="partner-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-14">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full text-[#2563EB] text-sm font-medium mb-4">
                <Link2 className="h-4 w-4" /> Integration Partners
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4" data-testid="partner-headline">
                Works with Leading Hospitality Platforms
              </h2>
            </div>
          </Reveal>

          <div className="flex flex-wrap justify-center gap-6">
            {partners.map((partner, i) => (
              <Reveal key={i} delay={i * 50}>
                <div className="flex items-center gap-4 px-6 py-4 bg-slate-50 border border-slate-200 rounded-xl hover:shadow-md transition-all" data-testid={`partner-${partner.name.toLowerCase().replace(/\s/g, '-')}`}>
                  <img src={partner.logo} alt={partner.name} className="h-10 w-auto object-contain rounded" />
                  <span className="text-base font-semibold text-slate-800">{partner.name}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Positioning Line ── */}
      <PositioningBanner />

      {/* ═══════════════════════════════════════════════════════
          SECTION 6 — FINAL CTA
      ═══════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-[#2563EB] via-[#1D4ED8] to-[#1E40AF]" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-5" data-testid="cta-headline">
              Not sure which system to use?
            </h2>
          </Reveal>
          <Reveal delay={60}>
            <p className="text-xl text-white/85 mb-10 max-w-2xl mx-auto leading-relaxed">
              Talk to us and we'll recommend the right setup for your property.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#2563EB] rounded-xl font-semibold text-lg hover:shadow-2xl hover:shadow-white/25 transition-all transform hover:-translate-y-0.5"
                data-testid="cta-consultation"
              >
                <Headphones className="h-5 w-5" /> Book Consultation
              </Link>
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/25 text-white rounded-xl font-semibold text-lg hover:bg-white/20 transition-all"
                data-testid="cta-get-started"
              >
                Get Started <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <HRPromoCard />

      <LandingFooter />
    </div>
  );
};

export default GetStarted;
